import { Game } from "../types/Game";

const thumb = (label: string, color: string) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 240'>
      <defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'>
        <stop offset='0%' stop-color='${color}'/>
        <stop offset='100%' stop-color='#1a1a2e'/>
      </linearGradient></defs>
      <rect width='400' height='240' fill='url(%23g)'/>
      <text x='50%' y='52%' text-anchor='middle' font-family='Segoe UI, sans-serif'
            font-size='34' font-weight='700' fill='white'>${label}</text>
      <text x='50%' y='78%' text-anchor='middle' font-family='Segoe UI, sans-serif'
            font-size='14' fill='rgba(255,255,255,0.75)'>Sign Practice</text>
    </svg>`
  )}`;

const asset = (gameId: string, label: string, color: string) => ({
  id: parseInt(gameId, 10),
  name: "thumbnail",
  description: `${label} thumbnail`,
  type: "string" as const,
  path: thumb(label, color) as "string",
  date_created: new Date().toISOString(),
  date_updated: new Date(),
});

export const dummyGames: Game[] = [
  {
    id: "1",
    name: "HangMan",
    description:
      "Classic hangman — sign letters with your hand to guess the word before the figure is drawn.",
    rules: "Pick a topic and a level, then sign one letter at a time to guess the word./nA letter in the word fills its blanks; a wrong letter adds a part to the figure. 6 wrong letters and you lose./nStuck? Use a hint (clues and letters, depending on the level) or skip the word.",
    type: "hangman",
    date_created: new Date().toISOString(),
    bank_id: 1,
    gameAssets: [asset("1", "Hangman", "#ff6b6b")],
  },
  {
    id: "2",
    name: "Wordle",
    description:
      "Guess the hidden word in 6 tries by signing each letter. Colours show which letters are right.",
    rules: "Pick a topic and a level. Sign a word letter by letter, then press Submit./nGreen = right letter in the right spot, yellow = in the word but in another spot./nTap a letter to sign it again. You have 6 tries, and hints when you are stuck.",
    type: "wordle",
    date_created: new Date().toISOString(),
    bank_id: 1,
    gameAssets: [asset("2", "Wordle", "#22c55e")],
  },
  {
    id: "3",
    name: "SpellingLetters",
    description: "Practice spelling individual letters with your webcam.",
    rules: "Sign the letter shown and hold it until the ring fills./nBeat the timer for each letter: Easy 10 s, Medium 6 s, Hard 3 s. 10 letters per round./nTap Show sign to see the handshape (it costs a point).",
    type: "spelling",
    date_created: new Date().toISOString(),
    bank_id: 1,
    gameAssets: [asset("3", "Spelling Letters", "#0ea5e9")],
  },
  {
    id: "4",
    name: "SpellingWords",
    description: "Spell whole words letter by letter using hand signs.",
    rules: "Spell the word by signing each letter in order, holding each until the ring fills./nYou have 10 seconds per letter. Harder levels use longer words./nTap Show sign to see the handshape (it costs a point).",
    type: "spelling",
    date_created: new Date().toISOString(),
    bank_id: 1,
    gameAssets: [asset("4", "Spelling Words", "#a855f7")],
  },
  {
    id: "5",
    name: "AlphabetQuiz",
    description:
      "Multiple-choice quiz: see a letter's sign, pick the matching letter from four options.",
    rules: "Look at the sign and tap the letter it shows./n10 rounds, 1 point for each right answer.",
    type: "quiz",
    date_created: new Date().toISOString(),
    bank_id: 1,
    gameAssets: [asset("5", "Alphabet Quiz", "#f59e0b")],
  },
  {
    id: "6",
    name: "MemoryMatch",
    description:
      "Flip cards to match each sign with its letter. Train your memory and letter recognition.",
    rules: "Flip two cards at a time to find each sign and its letter./nMatch all 8 pairs in as few moves as you can.",
    type: "memory",
    date_created: new Date().toISOString(),
    bank_id: 1,
    gameAssets: [asset("6", "Memory Match", "#ec4899")],
  },
  {
    id: "7",
    name: "SpeedSign",
    description:
      "Rapid-fire identification — how many letter signs can you recognize in 60 seconds?",
    rules: "Tap the letter that matches the sign, as fast as you can./nYou have 60 seconds. Right answers score; wrong ones count as misses.",
    type: "speed",
    date_created: new Date().toISOString(),
    bank_id: 1,
    gameAssets: [asset("7", "Speed Sign", "#ef4444")],
  },
  {
    id: "8",
    name: "NumberSigns",
    description:
      "Practice the number signs 0–9. Match the sign card to the right number.",
    rules: "Look at the hand sign and tap the number it shows./n10 rounds covering the numbers 0 to 9.",
    type: "numbers",
    date_created: new Date().toISOString(),
    bank_id: 1,
    gameAssets: [asset("8", "Number Signs", "#14b8a6")],
  },
  {
    id: "9",
    name: "FillInBlank",
    description:
      "A word with one missing letter shown as a sign — pick the letter to complete it.",
    rules: "One letter of the word is shown as a sign. Tap the letter that completes the word./n8 rounds, 1 point for each right answer.",
    type: "fill",
    date_created: new Date().toISOString(),
    bank_id: 1,
    gameAssets: [asset("9", "Fill in the Blank", "#6366f1")],
  },
  {
    id: "10",
    name: "SignBingo",
    description:
      "5×5 bingo grid of sign cards. Mark the called letter — first row, column, or diagonal wins.",
    rules: "A new letter is called every few seconds. Find its sign on your card and tap it./nComplete a row, a column or a diagonal to win.",
    type: "bingo",
    date_created: new Date().toISOString(),
    bank_id: 1,
    gameAssets: [asset("10", "Sign Bingo", "#84cc16")],
  },
];

export const featuredGameIds = ["5", "6", "7", "1", "2"];
export const newestGameId = "10";

export function findGameByName(name: string): Game | undefined {
  const normalized = name.replace(/\s+/g, "").toLowerCase();
  return dummyGames.find(
    (g) => g.name.replace(/\s+/g, "").toLowerCase() === normalized
  );
}
